import { Minus, TrendingDown, TrendingUp } from "lucide-react";
import React from "react";
import { TODAY } from "@/data/financial-systems";
import { useFormatting } from "@/hooks/useFormatting";
import { useTerminology } from "@/hooks/useTerminology";

interface VarianceRow {
  category: string;
  budgeted: number;
  actual: number;
  variance: number;
  variancePct: number;
  favourable: boolean | null; // null = neutral
  reason?: string;
}

export const VarianceAnalysisPreview: React.FC = () => {
  const { getForecastTerm, getCashMovementTerms, getVarianceTerm } =
    useTerminology();
  const { formatCurrency } = useFormatting();
  const terms = getCashMovementTerms();
  const forecastTerm = getForecastTerm(false);

  const periodLabel = TODAY.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });

  const rows: VarianceRow[] = [
    // Income lines
    {
      category: "Contract Revenue",
      budgeted: 385_000,
      actual: 362_400,
      variance: -22_600,
      variancePct: -5.9,
      favourable: false,
      reason: "BigCo Ltd milestone payment delayed to next month",
    },
    {
      category: "Retention Releases",
      budgeted: 28_000,
      actual: 31_500,
      variance: 3_500,
      variancePct: 12.5,
      favourable: true,
      reason: "Westfield project retention released early",
    },
    {
      category: "Other Income",
      budgeted: 3_667,
      actual: 4_100,
      variance: 433,
      variancePct: 11.8,
      favourable: true,
    },
    // Expense lines
    {
      category: "Materials",
      budgeted: 20_100,
      actual: 22_400,
      variance: -2_300,
      variancePct: -11.4,
      favourable: false,
      reason: "Steel price increase — supplier contract under review",
    },
    {
      category: "Subcontractors",
      budgeted: 13_400,
      actual: 12_800,
      variance: 600,
      variancePct: 4.5,
      favourable: true,
    },
    {
      category: "Equipment Hire",
      budgeted: 4_200,
      actual: 4_200,
      variance: 0,
      variancePct: 0,
      favourable: null,
    },
    {
      category: "Payroll & NI",
      budgeted: 18_760,
      actual: 18_760,
      variance: 0,
      variancePct: 0,
      favourable: null,
    },
    {
      category: "Office & Overheads",
      budgeted: 3_305,
      actual: 3_890,
      variance: -585,
      variancePct: -17.7,
      favourable: false,
      reason: "Emergency boiler repair — one-off",
    },
    {
      category: "VAT & Corporation Tax",
      budgeted: 1_700,
      actual: 1_700,
      variance: 0,
      variancePct: 0,
      favourable: null,
    },
  ];

  const totalBudgeted =
    rows.slice(0, 3).reduce((s, r) => s + r.budgeted, 0) -
    rows.slice(3).reduce((s, r) => s + r.budgeted, 0);
  const totalActual =
    rows.slice(0, 3).reduce((s, r) => s + r.actual, 0) -
    rows.slice(3).reduce((s, r) => s + r.actual, 0);
  const totalVariance = totalActual - totalBudgeted;

  const VarianceCell: React.FC<{ row: VarianceRow }> = ({ row }) => {
    if (row.favourable === null)
      return (
        <span className="text-muted-foreground flex items-center gap-1 justify-end">
          <Minus className="h-3 w-3" /> —
        </span>
      );
    return (
      <span
        className={`flex items-center gap-1 justify-end ${row.favourable ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}`}
      >
        {row.favourable ? (
          <TrendingUp className="h-3 w-3" />
        ) : (
          <TrendingDown className="h-3 w-3" />
        )}
        {formatCurrency(Math.abs(row.variance))} (
        {row.variancePct > 0 ? "+" : ""}
        {row.variancePct.toFixed(1)}%)
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Period header */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">
            Budgeted Net Position
          </p>
          <p className="text-xl font-bold">{formatCurrency(totalBudgeted)}</p>
        </div>
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">
            Actual Net Position
          </p>
          <p className="text-xl font-bold">{formatCurrency(totalActual)}</p>
        </div>
        <div
          className={`rounded-lg border p-4 text-center ${totalVariance >= 0 ? "bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800" : "bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800"}`}
        >
          <p className="text-xs text-muted-foreground mb-1">
            Total {getVarianceTerm(true)}
          </p>
          <p
            className={`text-xl font-bold ${totalVariance >= 0 ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}`}
          >
            {totalVariance >= 0 ? "+" : ""}
            {formatCurrency(totalVariance)}
          </p>
        </div>
      </div>

      {/* Variance table */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/60">
              <th className="px-4 py-3 text-left font-semibold">Category</th>
              <th className="px-4 py-3 text-right font-semibold">Budgeted</th>
              <th className="px-4 py-3 text-right font-semibold">Actual</th>
              <th className="px-4 py-3 text-right font-semibold">
                {getVarianceTerm(true)}
              </th>
              <th className="px-4 py-3 text-left font-semibold pl-6">Reason</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b bg-muted/20">
              <td
                colSpan={5}
                className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide"
              >
                {terms.in}
              </td>
            </tr>
            {rows.slice(0, 3).map((row, i) => (
              <tr
                key={i}
                className="border-b hover:bg-muted/30 transition-colors"
              >
                <td className="px-4 py-2.5">{row.category}</td>
                <td className="px-4 py-2.5 text-right">
                  {formatCurrency(row.budgeted)}
                </td>
                <td className="px-4 py-2.5 text-right font-medium">
                  {formatCurrency(row.actual)}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <VarianceCell row={row} />
                </td>
                <td className="px-4 py-2.5 pl-6 text-xs text-muted-foreground">
                  {row.reason ?? "—"}
                </td>
              </tr>
            ))}
            <tr className="border-b bg-muted/20">
              <td
                colSpan={5}
                className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide"
              >
                {terms.out}
              </td>
            </tr>
            {rows.slice(3).map((row, i) => (
              <tr
                key={i}
                className="border-b hover:bg-muted/30 transition-colors"
              >
                <td className="px-4 py-2.5">{row.category}</td>
                <td className="px-4 py-2.5 text-right">
                  {formatCurrency(row.budgeted)}
                </td>
                <td className="px-4 py-2.5 text-right font-medium">
                  {formatCurrency(row.actual)}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <VarianceCell row={row} />
                </td>
                <td className="px-4 py-2.5 pl-6 text-xs text-muted-foreground">
                  {row.reason ?? "—"}
                </td>
              </tr>
            ))}
            {/* Net totals row */}
            <tr className="border-t-2 bg-muted/60 font-semibold">
              <td className="px-4 py-3">Net Position</td>
              <td className="px-4 py-3 text-right">
                {formatCurrency(totalBudgeted)}
              </td>
              <td className="px-4 py-3 text-right">
                {formatCurrency(totalActual)}
              </td>
              <td className="px-4 py-3 text-right">
                <span
                  className={
                    totalVariance >= 0
                      ? "text-green-700 dark:text-green-400"
                      : "text-red-700 dark:text-red-400"
                  }
                >
                  {totalVariance >= 0 ? "+" : ""}
                  {formatCurrency(totalVariance)}
                </span>
              </td>
              <td className="px-4 py-3 pl-6" />
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Period: {periodLabel} · Actuals sourced from Access Financials · Budget
        sourced from Excel import · Favourable {getVarianceTerm(false)}s shown
        in green
      </p>
    </div>
  );
};
